import {useEffect, useState} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {t} from 'i18next';

import AurButton from '../../common/aur-button/AurButton';
import AurDataGrid from '../../common/aur-data-grid/AurDataGrid';
import {ReactComponent as DownloadIcon} from '../../../assests/svg/download-icon.svg';
import BarChartCell from '../../reports/HostingVolumeReport/BarChartCell';
import {
  downloadProcessingExceptionCsv,
  getProcessingException,
} from '../../../api/EcaManagementService';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  downloadResponseAsCSV,
  formateNumber,
  getProjectReportFileName,
} from '../../../utils/utils';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {useNavigate} from 'react-router-dom';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';

interface ProcessingExceptionsDialogProps {
  projectId: string | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  loadFileId?: number;
  projectName?: string | null;
}
interface Exceptions {
  processingExceptionId: number;
  name: string;
  count: number;
  mappedName: string;
}
interface ExceptionType {
  processingExceptionTypeId: string;
  name: string;
  processingException: Exceptions[];
}

interface ExceptionCount {
  totalDocument: number;
  totalException: number;
  maxCount: number;
}

const processingExceptionKeyMap: Record<string, string> = {
  'Already Resolved':
    'ecaHomepageModals.processingExceptionModal.exceptionNames.alreadyResolved',
  'Non Actionable Exceptions':
    'ecaHomepageModals.processingExceptionModal.exceptionNames.nonActionableExceptions',
  'Bad Extension':
    'ecaHomepageModals.processingExceptionModal.exceptionNames.badExtension',
  'Non-searchable PDFs':
    'ecaHomepageModals.processingExceptionModal.exceptionNames.nonSearchablePdfs',
  'Text Stripped':
    'ecaHomepageModals.processingExceptionModal.exceptionNames.textStripped',
  Unrecognized:
    'ecaHomepageModals.processingExceptionModal.exceptionNames.unrecognized',
};

const ProcessingExceptionsDialog: React.FC<ProcessingExceptionsDialogProps> = ({
  projectId,
  isOpen,
  onClose,
  loadFileId,
  projectName,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const {trackGAEvent} = useAnalytics();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [processingExceptions, setProcessingExceptions] = useState<
    ExceptionType[]
  >([]);
  const [exceptionCount, setExceptionCount] = useState<ExceptionCount>();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isClearAllLoading, setIsClearAllLoading] = useState<boolean>(false);

  const selectedClient = useSelector(
    (state: RootState) => state.selectedClient
  );

  const navigate = useNavigate();
  const {hasSearchesPerm} = usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);

  const fetchProcessingExceptions = async (params: string) => {
    setLoading(true);
    try {
      const response = await getProcessingException(params);
      setLoading(false);
      const exceptionCount = response?.data?.data.reduce(
        (acc: ExceptionCount, type: ExceptionType) => {
          type.processingException.forEach((exception: Exceptions) => {
            acc.totalException++;
            acc.totalDocument += exception.count;
            if (exception.count > acc.maxCount) {
              acc.maxCount = exception.count;
            }
          });
          return acc;
        },
        {totalException: 0, totalDocument: 0, maxCount: 0}
      );
      const processingExceptionData = response?.data?.data;
      processingExceptionData.forEach((exc: ExceptionType) => {
        exc.name = t(processingExceptionKeyMap[exc.name]) || exc.name;

        exc.processingException.sort((a, b) => b.count - a.count);
        exc.processingException.forEach((process: Exceptions, ind: number) => {
          process.processingExceptionId = ind;
          process.mappedName =
            t(processingExceptionKeyMap[process.name]) || process.name;
        });
      });

      setProcessingExceptions(processingExceptionData);
      setExceptionCount(exceptionCount);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    let paramString = `clientId=${selectedClient.clientId}&projectId=${projectId}`;

    if (loadFileId) {
      paramString += `&loadFilesId=${loadFileId}`;
    }
    fetchProcessingExceptions(paramString);
  }, [selectedClient.clientId, projectId, isOpen]);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const handleDownloadProcessingExceptionCsv = async () => {
    setIsClearAllLoading(true);

    try {
      let paramString = `clientId=${clientId}&projectId=${projectId}&SortColumn=processingexceptioncount&SortOrder=desc`;
      if (loadFileId) {
        paramString += `&loadFilesId=${loadFileId}`;
      }
      const response = await downloadProcessingExceptionCsv(paramString);
      downloadResponseAsCSV(
        response,
        getProjectReportFileName(
          projectName,
          t('ecaHomepageModals.processingExceptionModal.header')
        )
      );
    } catch (e) {
      console.log(e);
    } finally {
      setIsClearAllLoading(false);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
        file_extension: GA_FILE_EXTENTIONS.CSV,
        click: {
          name: t('gaTracking.global.downloadReport'),
          region: GA_REGIONS.MODAL,
          type: GA_CLICK_TYPES.BUTTON,
          action: t('gaTracking.global.downloadedReport'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
            label: t('gaTracking.ecaHomePage.processingExceptions'),
          }),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  };

  const handleCloseClick = () => {
    onClose();
  };

  const handleSelectionChange = (name: string, newSelection: any[]) => {
    setSelectedRows((prev) => {
      const filtered = prev.filter((row) => row.name !== name);

      const mapped = newSelection.map((item) => ({
        id: item,
        name,
      }));

      return [...filtered, ...mapped];
    });
  };

  const renderHeader = () => {
    return (
      <div className="header">
        <Typography className="typo-text headline3_size title-text" id="processing-exception-dialog-title">
          {t('ecaHomepageModals.processingExceptionModal.header')}
        </Typography>
      </div>
    );
  };

  const renderDocumentDetails = () => {
    return (
      <div className="document-details">
        <div className="number-wrap">
          <div className="right-align-center-y">
            <Typography className="typo-text display3_size">
              {formateNumber(exceptionCount?.totalException || 0)}
            </Typography>
            <Typography className="body2_size mist-color-700 mt-10">
              {t('ecaHomepageModals.processingExceptionModal.exceptions')}
            </Typography>
          </div>
          <div className="right-align-center-y ml-40">
            <Typography className="typo-text display3_size">
              {formateNumber(exceptionCount?.totalDocument || 0)}
            </Typography>
            <Typography className="body2_size mist-color-700 mt-10">
              {t('ecaHomepageModals.processingExceptionModal.totalDocument')}
            </Typography>
          </div>
        </div>
        <AurButton
          data-testid="download-button"
          onClick={handleDownloadProcessingExceptionCsv}
          startIcon={
            isClearAllLoading ? (
              <div className="color-loader">
                <AurLoadingIndicator
                  size={16}
                  className="progress-icon"
                  aria-hidden="true"
                />
              </div>
            ) : (
              <DownloadIcon aria-hidden="true" />
            )
          }
          type="continue"
          size="Medium"
          disabled={isClearAllLoading}
          label={t('common.downloadReport')}
          classNames="typo-text buttonMedium_size"
        />
      </div>
    );
  };

  const renderActionButtons = () => {
    return (
      <DialogActions className="footer-action-container">
        <div style={{display: 'flex', gap: '24px', marginBlock: '10px'}}>
          <AurButton
            classNames="typo-text"
            type="exit"
            size="Medium"
            onClick={handleCloseClick}
            label={t('common.close')}
            tabIndex={0}
            dataTestId="dialog-cancel-button"
          />
          {hasSearchesPerm && (
            <AurButton
              classNames="pagination-continue-button"
              type="continue"
              size="Medium"
              disabled={selectedRows.length === 0}
              onClick={() => {
                const selectedData = selectedRows
                  .map((item) => {
                    const tableData = processingExceptions.find(
                      (data) => data.name === item.name
                    );
                    if (tableData) {
                      const processingException =
                        tableData.processingException.find(
                          (pe) => pe.processingExceptionId === item.id
                        );
                      if (processingException) {
                        return {
                          ...item,
                          ...processingException,
                          processingExceptionTypeName: tableData.name,
                        };
                      }
                    }
                    return null;
                  })
                  .filter((item) => item !== null);

                navigate('/index-search', {
                  state: {
                    source: 'processingException',
                    selected: selectedData,
                  },
                });
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.SEARCH,
                  click: {
                    name: t('gaTracking.ecaHomePage.ViewInDoc'),
                    region: GA_REGIONS.MODAL,
                    type: GA_CLICK_TYPES.BUTTON,
                    action: t('gaTracking.ecaHomePage.searchDocuments'),
                  },
                  subPage: {
                    subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
                      label: t('gaTracking.ecaHomePage.processingExceptions'),
                    }),
                    subpage_type: GA_SUBPAGE_TYPE.MODAL,
                  },
                });
              }}
              label={t('label.viewInDocList')}
              tabIndex={2}
              dataTestId="dialog-confirm-button"
            />
          )}
        </div>
      </DialogActions>
    );
  };

  const tableColumns = [
    {
      field: 'mappedName',
      headerName: t('column.name'),
      width: 200,
      flex: 0.5,
      sortable: false,
    },
    {
      field: '',
      headerName: '',
      sortable: false,
      minWidth: 200,
      flex: 1.5,
      renderCell: (params: any) => {
        return (
          <div style={{position: 'relative', overflow: 'visible'}}>
            <BarChartCell
              value={params.row.count}
              maxValue={exceptionCount?.maxCount || 0}
              name={params.row.name}
              showLabel={true}
              fullWidthBar={true}
              showTooltip={false}
              theme="light"
            />
          </div>
        );
      },
    },
  ];

  const deriveClass = (ind: number) => {
    if (ind === 0) {
      return 'table-header border-radius-top';
    } else {
      return 'table-header';
    }
  };
  const deriveClassTable = (ind: number) => {
    if (ind === processingExceptions.length - 1) {
      return {
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
      };
    } else {
      return {borderRadius: '0px'};
    }
  };

  const renderTable = () => {
    return (
      <div className={`table-container`}>
        <div tabIndex={0} className="scroll-wrapper">
          {processingExceptions.map((exception: ExceptionType, index) => (
            <>
              <div
                className={deriveClass(index)}
                key={exception.processingExceptionTypeId}
              >
                <Typography className="typo-text label1_size">
                  {exception.name}
                </Typography>
              </div>
              <AurDataGrid
                style={deriveClassTable(index)}
                columnHeaderHeight={0}
                rows={exception.processingException}
                disableColumnSorting={true}
                visibleToolTip={true}
                columns={tableColumns}
                showPagination={false}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                getRowId={(row) => row.processingExceptionId}
                checkboxSelection={hasSearchesPerm}
                onSelectionChange={(newSelection) =>
                  handleSelectionChange(exception.name, newSelection)
                }
                disableRowSelectionOnClick={!hasSearchesPerm}
                addedInDialog
                classNameOfParentScollElement='scroll-wrapper'
              />
            </>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={isOpen}
      onClose={handleCloseClick}
      PaperProps={{className: 'evidence-source-dialog processing-exceptions'}}
      maxWidth={false}
      aria-labelledby='processing-exception-dialog-title'
    >
      <DialogContent className="model-content">
        <>
          {renderHeader()}
          {renderDocumentDetails()}
          {renderTable()}
        </>
      </DialogContent>
      {renderActionButtons()}
      <AurLoadingIndicator overlay={true} isOpen={loading} />
    </Dialog>
  );
};

export default ProcessingExceptionsDialog;
